/**
 * Przełącznik danych demonstracyjnych.
 *
 * Produkty oznaczone w bazie jako demo są w HQ po to, żeby pokazać system
 * komuś, kto go jeszcze nie zna. Po podpięciu pierwszego prawdziwego produktu
 * przestają być pomocą i stają się zanieczyszczeniem raportu — dlatego da się
 * je odciąć jednym kliknięciem, zanim ktokolwiek zdecyduje się je skasować.
 *
 * Wybór jest zapamiętywany w przeglądarce, bo to preferencja patrzącego,
 * a nie ustawienie organizacji: wspólnik ogląda demo, właściciel patrzy na
 * pieniądze, obaj na tym samym koncie.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";

const STORAGE_KEY = "folwark.hq.include_demo";

type DemoContextValue = {
  includeDemo: boolean;
  setIncludeDemo: (value: boolean) => void;
  /** Dokleja `include_demo=0` do ścieżki, gdy demo ma być odcięte. */
  scoped: (path: string) => string;
  /** Do klucza zapytania — inaczej React Query poda dane z poprzedniego trybu. */
  key: string;
};

const DemoContext = createContext<DemoContextValue | null>(null);

function readStored(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) !== "0";
  } catch {
    // Prywatne okno albo zablokowane dane witryny — wtedy po prostu pokazujemy
    // wszystko. Brak pamięci nie może wywrócić aplikacji.
    return true;
  }
}

export function DemoProvider({ children }: { children: ReactNode }) {
  const [includeDemo, setState] = useState(readStored);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, includeDemo ? "1" : "0");
    } catch {
      /* patrz wyżej */
    }
  }, [includeDemo]);

  const setIncludeDemo = useCallback((value: boolean) => setState(value), []);

  const scoped = useCallback(
    (path: string) =>
      includeDemo ? path : `${path}${path.includes("?") ? "&" : "?"}include_demo=0`,
    [includeDemo],
  );

  const value = useMemo<DemoContextValue>(
    () => ({ includeDemo, setIncludeDemo, scoped, key: includeDemo ? "z-demo" : "bez-demo" }),
    [includeDemo, setIncludeDemo, scoped],
  );

  return <DemoContext.Provider value={value}>{children}</DemoContext.Provider>;
}

export function useDemo(): DemoContextValue {
  const context = useContext(DemoContext);
  if (!context) throw new Error("useDemo poza DemoProvider");
  return context;
}
